import {
  DESKTOP_PRODUCTS_PER_ROW,
  PAGE_SIZE,
} from "@faire/retailer-shared/search/searchRequest";
import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpPageResultsProvider";
import { SearchBanners } from "@faire/retailer-visitor-shared/components/Banners/SearchBanners/SearchBanners";
import { SearchResultsBanner } from "@faire/retailer-visitor-shared/components/Banners/SearchBanners/SearchResultsBanner";
import { withFallback } from "@faire/retailer-visitor-shared/components/GhostLoading/helpers";
import { LoadingProductTileSkeleton } from "@faire/retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { TrackedLinkContext } from "@faire/retailer-visitor-shared/components/TrackedLink/index";
import { ProductPositioningContext } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/productTileContext";
import { SaveToBoardProvider } from "@faire/retailer-visitor-shared/features/RetailerBoards/Context";
import { ProductTileClickImpressionProvider } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/ProductTileClickImpressionProvider";
import { ProductTileViewImpressionProvider } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/ProductTileViewImpressionProvider";
import {
  IBannerData,
  withBanner,
} from "@faire/retailer-visitor-shared/lib/bannerUtils";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingSearchRelevanceUX } from "@faire/retailer-visitor-shared/settings/useSettingSearchRelevanceUX";
import { moveProductToFrontOfArray } from "@faire/retailer-visitor-shared/views/product/utils/moveProductToFrontOfArray";
import { MaybeRSCEfficientListLoader } from "@faire/retailer-visitor-shared/views/search-v2/util/MaybeRSCEfficientListLoader";
import { MaybeServerRenderedProductTileWrapper } from "@faire/retailer-visitor-shared/views/search-v2/util/MaybeServerRenderedProductTileWrapper";
import { SearchRelevanceCarousel } from "@faire/retailer-visitor-shared/views/search/__internal__/SearchProductsPage/__internal__/SearchRelevanceCarousel";
import { getGroupIndexFromEnum } from "@faire/retailer-visitor-shared/views/search/utils";
import { IRawQueryParamsProps } from "@faire/web--source/common/query-params/IQueryParamsProps";
import { withQueryParams } from "@faire/web--source/common/query-params/withQueryParams";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { trackProductTileRightClick } from "@faire/web-api--source/events/productTile/click/right";
import { trackRetailerOnboardingSearchPageHighlightedProductView } from "@faire/web-api--source/events/retailerOnboarding/view/searchPageHighlightedProduct";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IQueryProductsV2WithLeanTileResponse } from "@faire/web-api--source/indigofair/data/IQueryProductsV2WithLeanTileResponse";
import { ISuggestedC3Category } from "@faire/web-api--source/indigofair/data/ISuggestedC3Category";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreen } from "@faire/web-api--source/indigofair/data/ProductTileScreen";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { observer } from "mobx-react";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { withRouter } from "react-router-dom";
import styled, { css } from "styled-components";

import { SearchProductsResultsListFallback } from "./SearchProductsResultsListFallback";

interface IProps {
  isFromDiscoverURL?: boolean;
  isLoading?: boolean;
  queryString: string;
  containerName: string;
  products: ILeanProductTile[] | undefined;
  irrelevantProducts: ILeanProductTile[] | undefined;
  organicRelevanceClassification?: keyof typeof IQueryProductsV2WithLeanTileResponse.OrganicRelevanceClassification;
  productTokens?: (string | undefined)[];
  brandMap: Record<string, ILeanProductTile.IBrand | undefined> | undefined;
  productTileContainer: ProductTileContainer;
  bannerData: IBannerData<SearchResultsBanner>[];
  filterKeys?: string[];
  className?: string;
  pageNumber?: number;
  searchBannersOffset?: number;
  title?: string | undefined;
  requestId?: string;
  itemsPerRowResponsive: ResponsiveValues<number>;
  searchPageLoadTimingRef?: React.Ref<HTMLDivElement>;
  suggestedC3Categories?: ISuggestedC3Category[];
  productTileScreen: ProductTileScreen;
  productTileScreenContainer: ProductTileScreenContainer;
}

const SearchProductsResultsListBase: React.FC<
  IProps & IRawQueryParamsProps
> = ({
  containerName,
  queryString,
  queryParams,
  isLoading,
  products,
  irrelevantProducts,
  organicRelevanceClassification,
  productTokens,
  brandMap,
  bannerData,
  className,
  requestId,
  itemsPerRowResponsive,
  searchPageLoadTimingRef,
  productTileContainer,
  productTileScreen,
  productTileScreenContainer,
}) => {
  const isLoggedIn = useIsLoggedInRetailer();
  const itemsPerRow = useResponsiveValue(itemsPerRowResponsive);

  useAppSignUpPageResultsUpdateEffect({ productTiles: products });
  const isSearchRelevanceUXEnabled = useSettingSearchRelevanceUX();

  const eventContextString = `${productTileContainer}|${queryString}`;

  if (!isLoading && (!products || products.length === 0)) {
    return null;
  }

  const handleRightClick = (product?: ILeanProductTile.ILeanProduct) => () => {
    const eventPromotedByString = product?.promoted_by.join(" ") ?? "";
    if (productTileContainer && product) {
      trackProductTileRightClick(
        eventContextString,
        requestId ?? "",
        product?.token ?? "",
        eventPromotedByString,
        product.impression_id ?? ""
      );
    }
  };

  const getComponentToInsert = () => {
    if (!isSearchRelevanceUXEnabled) {
      return undefined;
    }
    if (irrelevantProducts && irrelevantProducts.length > 0) {
      return {
        component: renderIrrelevantCarousel(),
        groupIndex:
          getGroupIndexFromEnum(
            organicRelevanceClassification ??
              "ORGANIC_RELEVANCE_CLASSIFICATION_UNKNOWN"
          ) ?? 3,
      };
    }

    return undefined;
  };

  const renderIrrelevantCarousel = () => {
    return (
      <SearchRelevanceCarousel
        products={irrelevantProducts ?? []}
        brandMap={brandMap}
        itemsPerSlideResponsive={itemsPerRowResponsive}
        handleRightClick={handleRightClick}
        requestId={requestId}
        queryString={queryString}
        containerName={containerName}
        productTileScreen={productTileScreen}
        insertionIndex={
          getGroupIndexFromEnum(
            organicRelevanceClassification ??
              "ORGANIC_RELEVANCE_CLASSIFICATION_UNKNOWN"
          ) ?? 3
        }
      />
    );
  };

  const renderProduct = (
    productTile: ILeanProductTile | undefined,
    index?: number
  ) => {
    const brand = brandMap && brandMap[productTile?.product?.brand_token ?? ""];

    if (productTile === undefined && !isLoggedIn) {
      return (
        <TileWrapper $enableSearchResultCleanup={isLoggedIn}>
          <LoadingProductTileSkeleton />
        </TileWrapper>
      );
    }

    return (
      <ProductTileViewImpressionProvider
        context={context}
        requestId={requestId}
        screenContext={productTileScreen}
        containerContext={productTileScreenContainer}
        contextToken={queryString}
      >
        <ProductTileClickImpressionProvider
          context={context}
          productTileContainer={productTileContainer}
          requestId={requestId}
          containerName={containerName}
          screenContext={productTileScreen}
          containerContext={productTileScreenContainer}
          contextToken={queryString}
        >
          <TrackedLinkContext.Provider
            value={{
              type: IClickInfo.Type.PRODUCT,
              containerName,
              requestId: requestId,
            }}
          >
            <SaveToBoardProvider value={{ trackOrigin: "search" }}>
              <TileWrapper
                $enableSearchResultCleanup={isLoggedIn}
                onContextMenu={handleRightClick(productTile?.product)}
              >
                <ProductPositioningContext.Provider
                  value={{ index, itemsPerRow }}
                >
                  <MaybeServerRenderedProductTileWrapper
                    index={index ?? 0}
                    productTile={productTile}
                    brand={brand}
                    requestId={requestId}
                    productTokens={productTokens}
                  />
                </ProductPositioningContext.Provider>
              </TileWrapper>
            </SaveToBoardProvider>
          </TrackedLinkContext.Provider>
        </ProductTileClickImpressionProvider>
      </ProductTileViewImpressionProvider>
    );
  };

  const allProducts = ((): (ILeanProductTile | undefined)[] => {
    if (products && products.length > 0) {
      return withBanner(products, itemsPerRow, bannerData);
    }

    // During loading, set the same number of products as the search page, plus one extra row for buffer
    return withFallback(products, PAGE_SIZE + DESKTOP_PRODUCTS_PER_ROW);
  })();

  // only highlight product if it is first page (no page query param)
  const displayProducts: (ILeanProductTile | undefined)[] = queryParams.get(
    "page"
  )
    ? allProducts
    : moveProductToFrontOfArray<ILeanProductTile>(
        allProducts,
        queryParams.get("refP"),
        trackRetailerOnboardingSearchPageHighlightedProductView
      );

  const context = `${productTileContainer}|${queryString}` as const;

  return (
    <ErrorBoundary fallback={<SearchProductsResultsListFallback />}>
      <SearchListWrapper
        className={className}
        ref={searchPageLoadTimingRef}
        $columns={itemsPerRowResponsive}
      >
        <MaybeRSCEfficientListLoader
          requestId={requestId}
          itemsPerRowResponsive={itemsPerRowResponsive}
          displayProducts={displayProducts}
          renderProduct={renderProduct}
          componentToInsert={getComponentToInsert()}
        />
        <SearchBanners banners={bannerData} />
      </SearchListWrapper>
    </ErrorBoundary>
  );
};

export const SearchProductsResultsList = observer(
  withRouter(withQueryParams(SearchProductsResultsListBase))
);

const SearchListWrapper = styled.div<{
  $columns: ResponsiveValues<number>;
}>`
  position: relative;
  display: grid;
  column-gap: ${getSpacing("1x")};
  row-gap: ${getSpacing("3x")};

  ${responsiveMediaQueries.mobile} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.mobileAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.tablet} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.tabletAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.desktop} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.desktopAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.xlarge} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.xLargeAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.xxlarge} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.xxLargeDesktop}, minmax(0, 1fr)) [end]`};
  }
`;

const TileWrapper = styled(Column)<{
  $enableSearchResultCleanup: boolean;
}>`
  width: 100%;
  padding: ${({ $enableSearchResultCleanup }) =>
    $enableSearchResultCleanup ? 0 : getSpacing("1x", 4, 0)};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${({ $enableSearchResultCleanup }) =>
      $enableSearchResultCleanup ? 0 : getSpacing("1x", "1x", 0)};
  }
`;
